/* ===========================================================================
   MAVELY JEV SCORE  v3   (MV124)  \u2014  the Success Score on every deal card
   ===========================================================================
   v2 (score bar layer, colours from the JEV panel, SOLD OUT ribbon) with a
   new way of getting the scores, because v2 sometimes showed the cards
   without their score after a refresh:

     v2 asked mavely-dealsflow for fetch('/api/deals?source=deals') with
     credentials 'same-origin'. dealsflow answers that one ONLY from its
     memory, and HOLDS it while the memory is empty. On a slow host the Deals
     List's first request can leave before dealsflow is installed (the
     deferred scripts after the app module arrive late), so the memory stayed
     empty and v2 waited for ever: no score until the next refresh. v2 also
     skipped a reload inside a 3-second throttle without trying again, and
     waited 1 s before its first paint.

   v3:
     1. reads dealsflow's memory directly (MavelyDealsRefresh.peek()) at every
        paint: a card on screen came through that memory, so its list is there;
     2. only when cards are still unknown, fetches a list of its own
        ('/api/deals?source=deals&mvjev=3', never held, 20 s time limit);
     3. re-checks on its own timer while any card is unknown (0.5 s, 1 s, 2 s
        ... 15 s), so it never depends on the next change on the page;
     4. paints as soon as deal cards appear (no 1 s wait);
     5. also matches cards whose picture is an off-site address, a picture
        shared by several deals, or the placeholder shown when a picture did
        not load (by the deal's description).
   v2 is still on disk: point index.html back at it to roll back.

   Each deal analysed by Jev arrives from /api/deals with jev_score, 0 to 100:
     - the card's background is a horizontal bar, low colour on the left and
       high colour on the right, the high part as wide as the score; it fills
       in like a loading bar when the card appears;
     - "( 92 )  Score" in the right-hand column, above See Deal.
   A deal with no score yet is left exactly as it was.

   SAME RULES AS mavely-deals: nothing React made is moved, wrapped or removed.
   The score, the bar layer and the ribbon are appended and placed by CSS; the
   three original parts of the card only gain a data attribute.

   Console: __mvJev()
   =========================================================================== */
(function () {
  'use strict';

  if (window.__mvJevV3) return;
  window.__mvJevV3 = true;
  window.__mvJevV2 = true;               // stops v2 if both are on the page
  window.__mvJevV1 = true;               // stops v1 if both are on the page

  var CFG = (window.__mvJevCfg && typeof window.__mvJevCfg === 'object') ? window.__mvJevCfg : {};
  function hex(c, d) { return /^#[0-9a-f]{6}$/i.test(String(c || '')) ? String(c) : d; }
  var LOOK = {
    low: hex(CFG.low, '#f0262b'), mid: hex(CFG.mid, ''), high: hex(CFG.high, '#1fd15b'),
    soft: Math.max(0, Math.min(30, isFinite(+CFG.soft) && CFG.soft !== '' && CFG.soft !== null ? +CFG.soft : 6)),
    hot: CFG.hot === 'hot' ? 'hot' : 'score', sold: CFG.sold === 'none' || CFG.sold === 'hide' ? CFG.sold : 'badge',
    seot: CFG.seot === 'above' || CFG.seot === 'replace' ? CFG.seot : 'off'     // MV124: SEO title on the cards (opt-in)
  };

  /* e = { s: score or null, out: sold out }, or null for a deal a list has
     without a score (see put() for the shape of the two indexes). */
  var byImage = Object.create(null);
  var byDesc = Object.create(null);      // description -> same shape, keyed by picture (built on demand)
  var stats = { loads: 0, cards: 0, painted: 0, cleared: 0, memLists: 0, own: 0, ownFail: 0, rechecks: 0 };
  var LABEL = { en: 'Score', ar: '\u0627\u0644\u0646\u062a\u064a\u062c\u0629', es: 'Puntuaci\u00f3n', fr: 'Score' };
  var SOLD = { en: 'SOLD OUT', ar: '\u0646\u0641\u062f\u062a \u0627\u0644\u0643\u0645\u064a\u0629', es: 'AGOTADO', fr: '\u00c9PUIS\u00c9' };

  function lang() {
    var h = String(document.documentElement.getAttribute('lang') || '').toLowerCase().slice(0, 2);
    return LABEL[h] ? h : 'en';
  }

  /* ================================================================ data == */

  /* WHERE THE SCORES COME FROM (v3)
     1. mavely-dealsflow's memory, read synchronously with
        MavelyDealsRefresh.peek(). Every list the Deals List renders through
        dealsflow is put there BEFORE React gets it, so when a card is on
        screen its list is already in memory: no request, nothing to wait for.
     2. A list of our own, only for cards that no list in memory knows - the
        Deals List's first request left before dealsflow was installed (a slow
        host: the deferred scripts after the app module arrive late), or
        dealsflow is not on the page. It is NOT '/api/deals?source=deals' with
        credentials: dealsflow answers that one only from memory and HOLDS it
        while memory is empty, which is what left v2 waiting for ever.
     v2 also dropped a reload silently inside its 3 s throttle and then
     depended on the next DOM change to try again; v3 re-checks on a timer of
     its own while any card is unknown (0.5 s, 1 s, 2 s ... 15 s). */
  var memLists = [];                     // the list objects indexed last time
  var ownList = null, ownAt = 0, ownBusy = false, ownGap = 30000;
  var unknownSince = 0, checkTimer = null, checkMs = 500;

  function entryOf(d) {
    var hasS = d.jev_score !== undefined && d.jev_score !== null && d.jev_score !== '' && isFinite(+d.jev_score);
    var out = d.jev_stock === 'out' && LOOK.sold === 'badge';
    var t = (LOOK.seot !== 'off' && typeof d.seo_title === 'string') ? d.seo_title : '';
    return (hasS || out || t) ? { s: hasS ? +d.jev_score : null, out: out, t: t } : null;
  }

  function norm(t) { return String(t || '').replace(/\s+/g, ' ').trim(); }

  /* byImage[picture] = { e, n, d }: e = the first deal's entry, n = how many
     different descriptions share the picture, d = description -> entry (only
     made when n > 1). byDesc is the same by description, built only when a
     card needs it. One pass, no search inside the pass. */
  function put(map, key, sub, e) {
    var r = map[key];
    if (!r) { map[key] = { e: e, n: 1, k: sub, d: null }; return; }
    if (r.k === sub) { r.e = e; if (r.d) r.d[sub] = e; return; }      // a newer list for the same deal wins here too
    if (!r.d) { r.d = Object.create(null); r.d[r.k] = r.e; }
    if (!(sub in r.d)) r.n++;
    r.d[sub] = e;
  }

  function addList(map, j) {
    if (!j || !j.deals || !j.deals.length) return;
    var host = location.host;
    for (var i = 0; i < j.deals.length; i++) {
      var d = j.deals[i];
      if (!d || !d.image_path) continue;
      var p = String(d.image_path), desc = String(d.description || ''), e = entryOf(d);
      put(map, p, desc, e);
      if (p.charAt(0) !== '/') {                          // same site, written in full
        var m = /^https?:\/\/([^\/]+)(\/.*)$/i.exec(p);
        if (m && m[1] === host) put(map, m[2], desc, e);
      }
    }
  }

  var descReady = false;
  function descIndex() {
    if (descReady) return byDesc;
    var dmap = Object.create(null), all = ownList ? [ownList].concat(memLists) : memLists;
    for (var l = 0; l < all.length; l++) {
      var ds = all[l].deals || [];
      for (var i = 0; i < ds.length; i++) {
        var d = ds[i];
        if (d && d.image_path && d.description) put(dmap, norm(d.description), String(d.image_path), entryOf(d));
      }
    }
    byDesc = dmap;
    descReady = true;
    return dmap;
  }

  function rebuild() {
    var t0 = Date.now();
    var map = Object.create(null);
    if (ownList) addList(map, ownList);
    for (var i = 0; i < memLists.length; i++) addList(map, memLists[i]);   // memory is newer: it wins
    byImage = map;
    descReady = false;
    stats.loads++;
    stats.buildMs = Date.now() - t0;
  }

  /** The lists in dealsflow's memory; rebuilds the map when they changed. */
  function readMemory() {
    var api = window.MavelyDealsRefresh, p = null, lists = [];
    try { p = (api && typeof api.peek === 'function') ? api.peek() : null; } catch (e) { p = null; }
    if (p) {
      /* oldest data first, so the newest list wins in rebuild() (dealsflow's
         memory keeps a revalidated list at its first place) */
      var arr = [];
      for (var u in p) {
        var v = p[u] && p[u].data;
        if (v && v.deals && v.deals.length) arr.push([+(p[u].started || p[u].at || 0), arr.length, v]);
      }
      arr.sort(function (a, b) { return (a[0] - b[0]) || (a[1] - b[1]); });
      for (var q = 0; q < arr.length; q++) lists.push(arr[q][2]);
    }
    var same = lists.length === memLists.length;
    for (var i = 0; same && i < lists.length; i++) if (lists[i] !== memLists[i]) same = false;
    if (same) return false;
    memLists = lists;
    stats.memLists = lists.length;
    rebuild();
    return true;
  }

  /** Our own copy, for cards no list in memory knows. Never held, never for ever. */
  function fetchOwn() {
    var now = Date.now();
    if (ownBusy || now - ownAt < ownGap) return;
    ownBusy = true;
    stats.own++;
    var done = false;
    function finish(j) {
      if (done) return;
      done = true;
      ownBusy = false;
      ownAt = Date.now();
      if (j && j.deals && j.deals.length) { ownList = j; rebuild(); }
      else stats.ownFail++;
      paint();
      /* still unknown with a full list of our own: those pictures are in no
         list (another site's card, a rewritten src) - ask rarely after that */
      ownGap = unknownSince ? 300000 : 30000;
    }
    setTimeout(function () { finish(null); }, 20000);
    try {
      fetch('/api/deals?source=deals&mvjev=3', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return (r && r.ok) ? r.json() : null; })
        .then(finish, function () { finish(null); });
    } catch (e) { finish(null); }
  }

  /** A paint of our own while cards are unknown, whatever the DOM does. */
  function recheck() {
    if (checkTimer) return;
    stats.rechecks++;
    checkTimer = setTimeout(function () { checkTimer = null; try { paint(); } catch (e) {} }, checkMs);
    checkMs = Math.min(15000, checkMs * 2);
  }

  var PLACEHOLDER = /\/uploads\/deals\/default\.jpg$/;

  function pick(r, sub) {                  // the entry for this card from a record, or undefined
    if (r.n === 1) return r.e;
    if (r.d && sub in r.d) return r.d[sub];
    var same = true, first, k;
    for (k in r.d) { if (first === undefined) first = r.d[k]; else if (!sameEntry(first, r.d[k])) { same = false; break; } }
    return same ? first : undefined;
  }
  function sameEntry(a, b) { return (!a && !b) || (!!a && !!b && a.s === b.s && a.out === b.out && a.t === b.t); }

  /** undefined: no list knows this card; null: known, no score; else the entry.
      By picture first; by the description when several deals share the
      picture, or when the card's onError swapped a picture that did not load
      for the placeholder (often an off-site image_path). */
  function lookup(img, card) {
    var src = img.getAttribute('src') || '';
    var r = byImage[src];
    if (!r) {
      var m = /^https?:\/\/([^\/]+)(\/.*)$/i.exec(src);
      if (m && m[1] === location.host) r = byImage[m[2]];
    }
    if (r && r.n === 1 && !PLACEHOLDER.test(src)) return r.e;
    var p = card.querySelector('p[class*="line-clamp"]') || card.querySelector('p');
    var raw = p ? String(p.textContent || '') : '';
    if (r) { var v = pick(r, raw); if (v !== undefined) return v; }
    var t = norm(raw);
    if (t) {
      var dr = descIndex()[t];
      if (dr) { var w = (dr.d && src in dr.d) ? dr.d[src] : pick(dr, src); return w === undefined ? null : w; }
    }
    return r ? null : undefined;
  }

  /* =============================================================== cards == */

  function isSeeDeal(node) {
    if (!node || node.nodeType !== 1) return false;
    var tag = node.tagName;
    if (tag !== 'BUTTON' && tag !== 'A' && !(tag === 'DIV' && node.getAttribute('role') === 'button')) return false;
    if (node.querySelector && node.querySelector('img[alt="Deal product"]')) return false;
    var t = (node.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
    if (t.length > 24) return false;
    return t.indexOf('SEE DEAL') !== -1 || t.indexOf('\u0634\u0627\u0647\u062f') !== -1;
  }

  /** The white card: the innermost box holding this one deal picture and
      the See Deal control (the trash button stays outside it). */
  function cardOf(img) {
    var n = img;
    for (var up = 0; up < 8 && n.parentElement; up++) {
      n = n.parentElement;
      if (n.querySelectorAll('img[alt="Deal product"]').length !== 1) return null;
      for (var i = 0; i < n.children.length; i++) {
        if (isSeeDeal(n.children[i])) return { card: n, btn: n.children[i] };
      }
    }
    return null;
  }

  function directChild(card, node) {
    while (node && node.parentElement !== card) node = node.parentElement;
    return node;
  }

  var PARTS = ['img', 'mid', 'btn'];

  function clearSold(card) {
    if (!card.classList.contains('mvjv-sold')) return;
    card.classList.remove('mvjv-sold');
    var r = card.querySelectorAll('.mvjv-soldout');
    for (var i = 0; i < r.length; i++) r[i].parentNode.removeChild(r[i]);
  }

  function clear(card) {
    clearSold(card);
    if (!card.hasAttribute('data-mvjv')) return;
    card.removeAttribute('data-mvjv');
    card.classList.remove('mvjv-card', 'mvjv-go');
    for (var i = card.children.length - 1; i >= 0; i--) {
      var c = card.children[i];
      if (c.classList && (c.classList.contains('mvjv-pill') || c.classList.contains('mvjv-fill'))) card.removeChild(c);
      else if (c.hasAttribute && c.hasAttribute('data-mvjv-role')) c.removeAttribute('data-mvjv-role');
    }
    stats.cleared++;
  }

  /** The bar: a layer of its own behind the content (z-index -2 inside the
      card), so it also covers a hot card's background and the HOT DEAL badge
      (z-index 3+) stays on top of it. */
  function fillFor(card, want) {
    var f = null;
    for (var i = 0; i < card.children.length; i++) {
      if (card.children[i].classList && card.children[i].classList.contains('mvjv-fill')) { f = card.children[i]; break; }
    }
    if (!want) { if (f) card.removeChild(f); return null; }
    if (!f) {
      f = document.createElement('span');
      f.className = 'mvjv-fill' + (LOOK.mid ? ' mvjv-3c' : '');
      f.setAttribute('aria-hidden', 'true');
      card.appendChild(f);
    }
    return f;
  }

  function soldRibbon(card, imgBox) {
    card.classList.add('mvjv-sold');
    if (imgBox.querySelector('.mvjv-soldout')) return;
    var r = document.createElement('span');
    r.className = 'mvjv-soldout';
    r.setAttribute('data-no-i18n', '1');
    r.textContent = SOLD[lang()] || SOLD.en;
    imgBox.appendChild(r);
  }

  function pillFor(card) {
    for (var i = 0; i < card.children.length; i++) {
      if (card.children[i].classList && card.children[i].classList.contains('mvjv-pill')) return card.children[i];
    }
    var p = document.createElement('div');
    p.className = 'mvjv-pill';
    p.setAttribute('data-mvjv-role', 'pill');
    p.setAttribute('data-no-i18n', '1');
    p.setAttribute('translate', 'no');
    var num = document.createElement('span');
    num.className = 'mvjv-num';
    var lbl = document.createElement('span');
    lbl.className = 'mvjv-lbl';
    p.appendChild(num);
    p.appendChild(lbl);
    /* the card copies or opens the deal when clicked; the score does neither */
    p.addEventListener('click', function (e) { e.stopPropagation(); });
    card.appendChild(p);
    return p;
  }

  function band(s) { return s >= 67 ? 'hi' : (s >= 34 ? 'mid' : 'lo'); }

  /* MV124 \u2014 the SEO title on the card (JEV panel > SEO, off by default).
     'above': a line of its own above the text; 'replace': shown instead of
     the text by CSS only. React's text, the copy box and Copy are untouched. */
  function seoTitle(card, t) {
    var p = card.querySelector('p[class*="line-clamp"]');
    var above = card.querySelector('.mvseot-above');
    var rep = card.querySelector('[data-mv-seot]');
    if (!t || LOOK.seot === 'off' || !p) {
      if (above) above.parentNode.removeChild(above);
      if (rep) rep.removeAttribute('data-mv-seot');
      return;
    }
    if (LOOK.seot === 'replace') {
      if (above) above.parentNode.removeChild(above);
      if (rep && rep !== p) rep.removeAttribute('data-mv-seot');
      if (!p.style.getPropertyValue('--mvseot-fs')) {
        var cs = getComputedStyle(p);
        p.style.setProperty('--mvseot-fs', cs.fontSize);
        p.style.setProperty('--mvseot-lh', cs.lineHeight);
      }
      if (p.getAttribute('data-mv-seot') !== t) p.setAttribute('data-mv-seot', t);
      return;
    }
    if (rep) rep.removeAttribute('data-mv-seot');
    var box = p.parentElement;
    if (!box || !box.parentNode) return;
    if (!above) {
      above = document.createElement('div');
      above.className = 'mvseot-above';
      above.setAttribute('data-no-i18n', '1');
    }
    if (above.textContent !== t) above.textContent = t;
    if (above.parentNode !== box.parentNode || above.nextSibling !== box) box.parentNode.insertBefore(above, box);
    /* a Hot Deal's badge sits in a top corner (the left one in Arabic): the line stops before it */
    var badge = card.querySelector('.mv-hotbadge');
    var padR = 0, padL = 0;
    above.style.paddingRight = ''; above.style.paddingLeft = '';
    if (badge) {
      var a = above.getBoundingClientRect(), g = badge.getBoundingClientRect();
      if (g.width && a.top < g.bottom && a.bottom > g.top && a.right > g.left && a.left < g.right) {
        if (g.left + g.width / 2 >= a.left + a.width / 2) padR = Math.ceil(a.right - g.left + 6);   // badge on the right
        else padL = Math.ceil(g.right - a.left + 6);                                             // badge on the left (RTL)
      }
    }
    if (padR) above.style.paddingRight = padR + 'px';
    if (padL) above.style.paddingLeft = padL + 'px';
  }

  function decorate(img) {
    var found = cardOf(img);
    if (!found) return;
    var card = found.card;
    stats.cards++;
    var entry = lookup(img, card);
    if (LOOK.seot !== 'off') seoTitle(card, entry ? entry.t : '');
    if (entry === undefined || entry === null) {
      if (card.hasAttribute('data-mvjv') || card.classList.contains('mvjv-sold')) clear(card);
      return entry === undefined ? false : true;
    }
    if (entry.out) soldRibbon(card, directChild(card, img) || img.parentElement); else clearSold(card);
    if (entry.s === null) { if (card.hasAttribute('data-mvjv')) clear(card); if (entry.out) soldRibbon(card, directChild(card, img) || img.parentElement); return true; }
    var score = Math.max(0, Math.min(100, Math.round(entry.s)));

    // the three original parts, by what they hold (never by position)
    var imgBox = directChild(card, img), btn = found.btn, mid = null;
    for (var i = 0; i < card.children.length; i++) {
      var c = card.children[i];
      if (c === imgBox || c === btn) continue;
      if (c.classList && (c.classList.contains('mvjv-pill') || String(c.className).indexOf('mv-') === 0)) continue;
      if (String(c.className).indexOf('absolute') !== -1) continue;          // the copy warning
      if (c.tagName === 'DIV' && !mid) mid = c;
    }
    if (!imgBox || !mid) return false;
    var roles = [imgBox, mid, btn];
    for (var r = 0; r < roles.length; r++) {
      if (roles[r].getAttribute('data-mvjv-role') !== PARTS[r]) roles[r].setAttribute('data-mvjv-role', PARTS[r]);
    }

    var pill = pillFor(card);
    var l = lang();
    var numText = '( ' + score + ' )';
    if (pill.firstChild.textContent !== numText) pill.firstChild.textContent = numText;
    if (pill.lastChild.textContent !== LABEL[l]) pill.lastChild.textContent = LABEL[l];
    pill.setAttribute('aria-label', LABEL[l] + ' ' + score + '/100');
    pill.title = 'AI Success Score ' + score + '/100';
    var b = band(score);
    if (pill.getAttribute('data-band') !== b) pill.setAttribute('data-band', b);

    /* a hot card keeps its fire background when that look is chosen */
    var hot = card.classList.contains('mv-hotshell') || card.hasAttribute('data-mv-hot');
    var fill = fillFor(card, !(hot && LOOK.hot === 'hot'));
    var same = card.getAttribute('data-mvjv') === String(score);
    if (same && (!fill || fill.style.getPropertyValue('--mvjv-fill') === score + '%')) { card.classList.add('mvjv-card'); return true; }
    var first = !card.hasAttribute('data-mvjv') || (fill && !fill.style.getPropertyValue('--mvjv-fill'));
    card.setAttribute('data-mvjv', String(score));
    card.classList.add('mvjv-card');
    if (fill) {
      if (first && !reduce()) {
        // the "loading" fill: from all low colour to the score
        fill.style.setProperty('--mvjv-fill', '0%');
        fill.classList.remove('mvjv-go');
        void fill.offsetWidth;
        requestAnimationFrame(function () {
          fill.classList.add('mvjv-go');
          fill.style.setProperty('--mvjv-fill', score + '%');
        });
      } else {
        fill.classList.add('mvjv-go');
        fill.style.setProperty('--mvjv-fill', score + '%');
      }
    }
    stats.painted++;
    return true;
  }

  function reduce() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  var hasCards = true;
  function paint() {
    var imgs = document.querySelectorAll('img[alt="Deal product"]');
    hasCards = imgs.length > 0;
    if (!imgs.length) { unknownSince = 0; return; }
    readMemory();
    var unknown = 0;
    for (var i = 0; i < imgs.length; i++) {
      try { if (decorate(imgs[i]) === false) unknown++; } catch (e) {}
    }
    if (!unknown) { unknownSince = 0; checkMs = 500; return; }
    /* A card is only drawn after its list went through dealsflow's memory, so
       an unknown card means that list never did. Our own copy at once when
       the memory is empty, after 1.5 s otherwise. */
    var now = Date.now();
    if (!unknownSince) unknownSince = now;
    if (!memLists.length || now - unknownSince >= 1500) fetchOwn();   // nothing in memory: nothing to wait for
    recheck();
  }

  /* ============================================================== styles == */

  function styles() {
    if (document.getElementById('mvjv-css')) return;
    try { if (window.CSS && CSS.registerProperty) CSS.registerProperty({ name: '--mvjv-fill', syntax: '<percentage>', inherits: false, initialValue: '0%' }); } catch (e) {}
    /* The redesign gives the card a border with
         html[data-mv-rd] .mv-rd-dl div[class*="bg-[var(--card)]"] {... !important}
       (specificity 0,3,2), so this selector is stronger (0,5,0). */
    var P = ':root .mvjv-card.mvjv-card[data-mvjv]';
    var soft = LOOK.soft + '%';
    var stops = LOOK.mid
      ? 'var(--mvjv-low) 0%,var(--mvjv-low) calc(100% - var(--mvjv-fill) - ' + soft + ' - 8%),var(--mvjv-mid) calc(100% - var(--mvjv-fill)),' +
        'var(--mvjv-high) calc(100% - var(--mvjv-fill) + ' + soft + ' + 8%),var(--mvjv-high) 100%'
      : 'var(--mvjv-low) 0%,var(--mvjv-low) calc(100% - var(--mvjv-fill) - ' + soft + '),' +
        'var(--mvjv-high) calc(100% - var(--mvjv-fill) + ' + soft + '),var(--mvjv-high) 100%';
    var css = [
      ':root{--mvjv-low:' + LOOK.low + ';--mvjv-high:' + LOOK.high + ';--mvjv-mid:' + (LOOK.mid || LOOK.high) + '}',
      '.mvjv-card{display:grid!important;grid-template-columns:auto minmax(0,1fr) auto;grid-template-rows:auto auto;',
      'gap:4px 10px!important;align-items:center;isolation:isolate;position:relative}',
      P + '{border-color:rgba(0,0,0,.14)!important}',
      '.mvjv-fill{--mvjv-fill:0%;position:absolute;top:0;right:0;bottom:0;left:0;z-index:-2;border-radius:inherit;pointer-events:none;',
      'background:linear-gradient(var(--mvjv-dir,90deg),' + stops + ')}',
      '.mvjv-fill.mvjv-go{transition:--mvjv-fill 1.4s cubic-bezier(.22,.8,.28,1)}',
      /* right to left: the score column is on the left, so the high colour is too */
      '[dir=rtl] .mvjv-fill{--mvjv-dir:270deg}',
      '.mvjv-card>[data-mvjv-role=img]{grid-column:1;grid-row:1/span 2}',
      '.mvjv-card>[data-mvjv-role=mid]{grid-column:2;grid-row:1/span 2;min-width:0}',
      '.mvjv-card>[data-mvjv-role=pill]{grid-column:3;grid-row:1;align-self:end}',
      '.mvjv-card>[data-mvjv-role=btn]{grid-column:3;grid-row:2;align-self:start}',
      '.mvjv-pill{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0;',
      'min-width:64px;padding:3px 6px 2px;border-radius:12px;background:rgba(255,255,255,.62);',
      'box-shadow:inset 0 0 0 1px rgba(255,255,255,.55);color:#0b0f14;line-height:1;cursor:default;user-select:none}',
      '.mvjv-num{font-weight:900;font-size:18px;letter-spacing:.5px;white-space:nowrap;font-variant-numeric:tabular-nums}',
      '.mvjv-lbl{font-weight:800;font-size:10px;margin-top:1px;color:#0b7a34}',
      '.mvjv-pill[data-band=mid] .mvjv-lbl{color:#8a5a00}',
      '.mvjv-pill[data-band=lo] .mvjv-lbl{color:#b3121a}',
      /* a hot card keeps its own background and its badge at the top corner */
      '.mvjv-card.mv-hotshell>[data-mvjv-role=pill],.mvjv-card[data-mv-hot]>[data-mvjv-role=pill]{margin-top:30px}',
      /* sold out: a ribbon across the picture, the picture in grey */
      '.mvjv-soldout{position:absolute;left:-6px;right:-6px;top:42%;z-index:6;padding:3px 0;text-align:center;pointer-events:none;',
      'font-weight:900;font-size:10px;letter-spacing:.08em;color:#fff;background:rgba(17,24,39,.88);transform:rotate(-12deg);',
      'box-shadow:0 2px 6px rgba(0,0,0,.35)}',
      '.mvjv-sold img[alt="Deal product"]{filter:grayscale(.85) opacity(.8)}',
      '.mvseot-above{font-weight:800;font-size:11.5px;line-height:1.3;margin:0 0 3px;color:inherit;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}',
      /* stronger than the redesign's own "font: 600 11.5px ... !important" on the card text */
      ':root p[data-mv-seot][data-mv-seot][data-mv-seot]{font-size:0!important}',
      ':root p[data-mv-seot][data-mv-seot][data-mv-seot]::before{content:attr(data-mv-seot);font-size:var(--mvseot-fs,12px);line-height:var(--mvseot-lh,1.35)}',
      '@media (max-width:380px){.mvjv-pill{min-width:54px}.mvjv-num{font-size:16px}}',
      '@media (prefers-reduced-motion:reduce){.mvjv-fill.mvjv-go{transition:none}}'
    ].join('');
    var st = document.createElement('style');
    st.id = 'mvjv-css';
    st.textContent = css;
    (document.head || document.documentElement).appendChild(st);
  }

  /* ============================================================== driver == */

  var due = null;
  /* A page with no deal cards (the dashboard, with thousands of rows that
     change all the time) is looked at once a second at most, not on every
     change. */
  function schedule(ms) {
    var wait = ms || (hasCards ? 120 : 1000);
    if (due) {
      if (wait >= dueMs) return;
      clearTimeout(due);              // deal cards just arrived: do not sit out the 1 s idle wait
    }
    dueMs = wait;
    due = setTimeout(function () { due = null; try { paint(); } catch (e) {} }, wait);
  }
  var dueMs = 0;

  function holdsCard(node) {
    try {
      if (node.tagName === 'IMG') return node.getAttribute('alt') === 'Deal product';
      return !!(node.firstElementChild && node.querySelector('img[alt="Deal product"]'));
    } catch (e) { return false; }
  }

  function start() {
    styles();
    schedule(50);
    if (window.MutationObserver) {
      new MutationObserver(function (recs) {
        var any = false;
        for (var i = 0; i < recs.length; i++) {
          var r = recs[i];
          if (r.type === 'attributes') {
            if (r.target && r.target.tagName === 'IMG' && r.target.getAttribute('alt') === 'Deal product') { schedule(); return; }
            if (r.attributeName === 'lang') { schedule(); return; }
            continue;
          }
          for (var n = 0; n < r.addedNodes.length; n++) {
            var node = r.addedNodes[n];
            if (node.nodeType !== 1) continue;
            if (node.classList && (node.classList.contains('mvjv-pill') || node.classList.contains('mvjv-fill'))) continue;
            if (hasCards) { schedule(); return; }
            /* no cards yet: the 1 s idle wait, unless this change brought deal cards */
            if (holdsCard(node)) { schedule(16); return; }
            any = true;
          }
        }
        if (any) schedule();
      }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'lang'] });
    }
    // a page left open picks up lists dealsflow re-read since it was loaded
    setInterval(function () { if (!document.hidden) schedule(); }, 90000);
  }

  window.__mvJev = function () {
    return { version: 3, look: LOOK, known: Object.keys(byImage).length, ownList: !!ownList, unknownSince: unknownSince, stats: stats };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(start, 0); }, { once: true });
  } else {
    start();
  }
})();
